import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Scale, Droplets, HeartPulse, Pill, Camera, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const C = {
  bg: '#FDFBF7', card: '#FFFFFF', border: '#E8E3DB',
  text: '#1A1A18', textSec: '#6A6560', muted: '#9A9590',
  accent: '#C67A5C', green: '#5A8C6A',
};

const TOOLS = [
  { key: 'weight_tracking', icon: Scale, label: 'Weight', action: 'weight-modal', color: '#C67A5C' },
  { key: 'hydration', icon: Droplets, label: 'Hydration', route: '/hydration', color: '#4A90D9' },
  { key: 'symptoms', icon: HeartPulse, label: 'Symptoms & Insights', route: '/symptom-journal', color: '#D4736A' },
  { key: 'medications', icon: Pill, label: 'Medications', route: '/medications', color: '#6B9E8A' },
  { key: 'progress_photos', icon: Camera, label: 'Progress Photos', route: '/progress-photos', color: '#9B59B6' },
  { key: 'activity', icon: BarChart3, label: 'Activity', route: '/exercise', color: '#E5A84B' },
];

export function HealthToolsSection() {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/health-tools/status`, authHeaders());
      setData(res.data);
    } catch (err) {
      console.warn('HealthTools: status fetch failed:', err.message);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchStatus();
    const handler = () => fetchStatus();
    window.addEventListener('nuri-data-refresh', handler);
    window.addEventListener('water-logged', handler);
    return () => {
      window.removeEventListener('nuri-data-refresh', handler);
      window.removeEventListener('water-logged', handler);
    };
  }, [fetchStatus]);

  const handleToolClick = (tool) => {
    if (tool.action === 'weight-modal') {
      window.dispatchEvent(new CustomEvent('nuri-open-weight'));
    } else if (tool.route) {
      navigate(tool.route);
    }
  };

  if (!data) return null;

  const { toggles, status } = data;
  const visibleTools = TOOLS.filter(t => {
    // Activity is always visible (not behind toggles)
    if (t.key === 'activity') return true;
    return toggles[t.key];
  });

  if (visibleTools.length === 0) return null;

  return (
    <div className="nuri-reveal nuri-d5" style={{ marginBottom: 24 }} data-testid="health-tools-section">
      <p style={{
        fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 400,
        color: C.text, margin: '0 0 14px',
      }}>
        Health Tools
      </p>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10,
      }}>
        {visibleTools.map(tool => {
          const Icon = tool.icon;
          const st = status[tool.key] || {};
          const statusText = st.text || '';
          return (
            <button
              key={tool.key}
              data-testid={`health-tool-${tool.key}`}
              onClick={() => handleToolClick(tool)}
              className="rounded-[16px] border backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] bg-white/[0.85] border-white/30 dark:bg-[#141C22]/[0.85] dark:border-white/10 active:scale-[0.97] transition-transform"
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '12px 8px', cursor: 'pointer', textAlign: 'center',
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                background: `${tool.color}12`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 6,
              }}>
                <Icon style={{ width: 20, height: 20, color: tool.color }} />
              </div>
              <p style={{
                fontSize: 11, fontWeight: 600, margin: 0,
                color: C.text, fontFamily: 'var(--font-sans)',
              }}>{tool.label}</p>
              {statusText && (
                <p style={{
                  fontSize: 9, margin: '2px 0 0', color: C.muted,
                  fontFamily: 'var(--font-sans)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}>{statusText}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
